import {
  Activity,
  CheckCircle2,
  CircleDot,
  Database,
  FileText,
  Loader2,
  Server,
  Wrench,
} from "lucide-react";
import type { ComponentType } from "react";
import { useMemo } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageContainer } from "@/components/PageContainer";
import { Skeleton } from "@/components/ui/skeleton";
import { useFeed } from "@/hooks/useFeed";
import { useModuli } from "@/hooks/useModuli";
import { cn } from "@/lib/utils";
import type { FeedTipo, VoceFeed } from "@/types/api";

const FEED_ICON: Record<FeedTipo, ComponentType<{ className?: string }>> = {
  completato: CheckCircle2,
  in_corso: Loader2,
  fix: Wrench,
  dati: Database,
  documenti: FileText,
  infra: Server,
  altro: CircleDot,
};

function dayKey(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function AttivitaPage() {
  const { data: feed = [], isLoading } = useFeed({ limit: 100 });
  const { data: moduli = [] } = useModuli();

  const moduloByid = useMemo(
    () => Object.fromEntries(moduli.map((m) => [m.id, m])),
    [moduli]
  );

  const grouped = useMemo(() => {
    const map = new Map<string, VoceFeed[]>();
    for (const v of feed) {
      // La giornata è quella in cui il lavoro è stato fatto, non quella in cui
      // lo abbiamo pubblicato: smaltendo l'arretrato tutto in un pomeriggio,
      // `pubblicato_il` ammassava mesi di lavoro su un giorno solo.
      const key = dayKey(v.git_committed_at);
      const arr = map.get(key) ?? [];
      arr.push(v);
      map.set(key, arr);
    }
    return Array.from(map.entries());
  }, [feed]);

  return (
    <PageContainer>
      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Sezione</p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">Attività</h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Cronologia delle attività del progetto, per giorno in cui il lavoro è stato
          svolto: completamenti, lavori in corso, fix, dati, documenti e operazioni
          infrastrutturali.
        </p>
      </header>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : grouped.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Nessuna attività ancora"
          description="Non ci sono ancora voci di attività pubblicate. Compariranno qui man mano che il lavoro avanza."
        />
      ) : (
        <div className="space-y-8">
          {grouped.map(([day, voci]) => (
            <section key={day} className="space-y-3">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground capitalize">
                {day}
              </h2>
              <ul className="rounded-lg border bg-card divide-y">
                {voci.map((v) => {
                  const Icon = FEED_ICON[v.tipo];
                  const modulo = v.modulo_id ? moduloByid[v.modulo_id] : undefined;
                  return (
                    <li key={v.id} className="flex gap-3 items-start px-4 py-3 text-sm">
                      <Icon
                        className={cn(
                          "size-4 shrink-0 mt-0.5 text-muted-foreground",
                          v.tipo === "in_corso" && "animate-spin",
                          v.tipo === "completato" && "text-success"
                        )}
                      />
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="font-medium leading-snug">{v.titolo_pubblicato}</p>
                        {v.corpo_pubblicato && (
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {v.corpo_pubblicato}
                          </p>
                        )}
                        {modulo && (
                          <p className="text-[11px] text-muted-foreground">
                            <span className="font-mono">{modulo.codice}</span> · {modulo.nome}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
